import React from "react";
import "./ProfessionalArc.css";

const ENTRIES = [
  {
    num: "01",
    type: "featured",
    title: "Popup Builder",
    titleUrl: "https://www.joomshaper.com/page-builder/popups",
    meta: "v5.4.0 · October 2024",
    desc: "Designed a full popup editor inside the page builder — triggers, overlays, conditions, premade layouts, and the same 50+ add-on library. Joomla users stopped needing a separate popup plugin.",
    tags: ["UI/UX design", "Add-on integration", "Trigger logic"],
    links: [
      { label: "Release notes", url: "https://www.joomshaper.com/blog/sp-page-builder-v5-4-0" },
      { label: "Product page", url: "https://www.joomshaper.com/page-builder/popups" },
    ],
  },
  {
    num: "02",
    type: "compact",
    side: "left",
    title: "Dynamic Content",
    titleUrl: "https://www.joomshaper.com/page-builder/dynamic-content",
    meta: "v5.5.0 · February 2025",
    desc: "Centralized content management with collections, custom fields, presets, and reference linking. Index and detail pages with filtering and sorting — a mini CMS inside a CMS.",
    link: { label: "Release notes", url: "https://www.joomshaper.com/blog/sp-page-builder-v5-5-0" },
  },
  {
    num: "03",
    type: "compact",
    side: "right",
    title: "Dynamic Content Filter",
    titleUrl: "https://www.joomshaper.com/blog/sp-page-builder-v5-6-0",
    meta: "v5.6.0 · May 2025",
    desc: "Search and filtering interfaces for dynamic collections — field-wise filters with checkboxes, radio buttons, text input, and configurable styling to match any site design.",
    link: { label: "Release notes", url: "https://www.joomshaper.com/blog/sp-page-builder-v5-6-0" },
  },
  {
    num: "04",
    type: "hero",
    title: "SP Page Builder 6.0",
    titleUrl: "https://www.joomshaper.com/page-builder",
    meta: "v6.0 · September 2025",
    desc: "Integrated native articles into Dynamic Content with ready-to-go index and detail layouts. Introduced a native commenting system with full moderation, reply threading, and reaction support. Shipped with Joomla 6 compatibility and performance improvements across the board.",
    pills: ["Integration", "Native comments", "Joomla 6"],
    link: { label: "Release notes", url: "https://www.joomshaper.com/blog/sp-page-builder-6" },
  },
  {
    num: "05",
    type: "compact",
    side: "left",
    title: "Rating & Expanded Comments",
    titleUrl: "https://www.joomshaper.com/blog/sp-page-builder-v6-1-0",
    meta: "v6.1.0 · October 2025",
    desc: "Rating field for Dynamic Content, expanded native comments beyond blogs to all dynamic detail pages, multilingual support, and load-more pagination.",
    link: { label: "Release notes", url: "https://www.joomshaper.com/blog/sp-page-builder-v6-1-0" },
  },
  {
    num: "06",
    type: "compact",
    side: "right",
    title: "Page Versioning & Accessibility",
    titleUrl: "https://www.joomshaper.com/blog/sp-page-builder-v6-3-1",
    meta: "v6.3.1 · March 2026",
    desc: "Layout version history with preview, restore, rename, and manual version creation with notes. Plus alt-text for dynamic galleries and accessibility improvements.",
    link: { label: "Release notes", url: "https://www.joomshaper.com/blog/sp-page-builder-v6-3-1" },
  },
  {
    num: "07",
    type: "featured",
    title: "Power Admin",
    titleUrl: "https://www.joomshaper.com/power-admin",
    meta: "v1.0.0-beta.1 · March 2026",
    desc: "A free Joomla backend experience with a clean login, minimal dashboard, draggable modules, quick-access overview cards, centralized content management, and a refreshed media manager — originally envisioned in 2017, finally shipped.",
    tags: ["System re-architecture", "Dashboard UX", "Free beta"],
    links: [
      { label: "Release notes", url: "https://www.joomshaper.com/blog/first-look-into-power-admin" },
      { label: "Product page", url: "https://www.joomshaper.com/power-admin" },
    ],
  },
];

const FeaturedEntry = ({ entry }) => (
  <div className="pro-arc__featured">
    <span className="pro-arc__big-num">{entry.num}</span>
    <div className="pro-arc__featured-body">
      <h3 className="pro-arc__feat-title">
        <a href={entry.titleUrl} target="_blank" rel="noopener noreferrer">{entry.title}</a>
      </h3>
      <span className="pro-arc__feat-meta">{entry.meta}</span>
      <p className="pro-arc__feat-desc">{entry.desc}</p>
      <div className="pro-arc__tags">
        {entry.tags.map((tag, i) => (
          <React.Fragment key={tag}>
            {i > 0 && <span className="dot">·</span>}
            <span>{tag}</span>
          </React.Fragment>
        ))}
      </div>
      <div className="pro-arc__links">
        {entry.links.map((lk, i) => (
          <React.Fragment key={lk.label}>
            {i > 0 && <span className="dot">·</span>}
            <a href={lk.url} target="_blank" rel="noopener noreferrer">
              {lk.label} →
            </a>
          </React.Fragment>
        ))}
      </div>
    </div>
  </div>
);

const CompactEntry = ({ entry }) => (
  <div className={`pro-arc__compact pro-arc__compact--${entry.side}`}>
    <span className="pro-arc__compact-num">{entry.num}</span>
    <h4 className="pro-arc__compact-title">
      <a href={entry.titleUrl} target="_blank" rel="noopener noreferrer">{entry.title}</a>
    </h4>
    <span className="pro-arc__compact-meta">{entry.meta}</span>
    <p className="pro-arc__compact-desc">{entry.desc}</p>
    <div className="pro-arc__compact-link">
      <a href={entry.link.url} target="_blank" rel="noopener noreferrer">
        {entry.link.label} →
      </a>
    </div>
  </div>
);

const HeroEntry = ({ entry }) => (
  <div className="pro-arc__hero">
    <span className="pro-arc__hero-num">{entry.num}</span>
    <div className="pro-arc__hero-body">
      <h3 className="pro-arc__hero-title">
        <a href={entry.titleUrl} target="_blank" rel="noopener noreferrer">{entry.title}</a>
      </h3>
      <span className="pro-arc__hero-meta">{entry.meta}</span>
      <p className="pro-arc__hero-desc">{entry.desc}</p>
      <div className="pro-arc__hero-pills">
        {entry.pills.map((pill) => (
          <span key={pill} className="pro-arc__pill">{pill}</span>
        ))}
      </div>
      <div className="pro-arc__hero-link">
        <a href={entry.link.url} target="_blank" rel="noopener noreferrer">
          {entry.link.label} →
        </a>
      </div>
    </div>
  </div>
);

const ProfessionalArc = () => {
  const featured1 = ENTRIES[0];
  const compact1 = [ENTRIES[1], ENTRIES[2]];
  const hero = ENTRIES[3];
  const compact2 = [ENTRIES[4], ENTRIES[5]];
  const featured2 = ENTRIES[6];

  return (
    <section id="professional-arc" className="pro-arc">
      {/* Header */}
      <div className="pro-arc__head">
        <div className="pro-arc__head-left">
          <span className="pro-arc__kick">Professional Arc</span>
          <h2 className="pro-arc__h1">
            Twenty eight months<br />inside <a href="https://www.joomshaper.com" target="_blank" rel="noopener noreferrer">JoomShaper</a>
          </h2>
        </div>
        <div className="pro-arc__head-right">
          <span className="pro-arc__hm1">Software Engineering @ <a href="https://www.ollyo.com" target="_blank" rel="noopener noreferrer">Ollyo</a></span>
          <span className="pro-arc__hm2">7 major releases · Jan 2024 – Mar 2026</span>
          <span className="pro-arc__hm3"><a href="https://www.joomshaper.com/page-builder" target="_blank" rel="noopener noreferrer">SP Page Builder</a> + <a href="https://www.joomshaper.com/power-admin" target="_blank" rel="noopener noreferrer">Power Admin</a></span>
        </div>
      </div>

      {/* Rule */}
      <div className="pro-arc__rule">
        <div className="pro-arc__rule-line" />
      </div>

      {/* 01 — Featured */}
      <FeaturedEntry entry={featured1} />

      {/* 02 + 03 — Compact grid */}
      <div className="pro-arc__grid">
        {compact1.map((e) => (
          <CompactEntry key={e.num} entry={e} />
        ))}
      </div>

      {/* 04 — Dark hero */}
      <HeroEntry entry={hero} />

      {/* 05 + 06 — Compact grid */}
      <div className="pro-arc__grid">
        {compact2.map((e) => (
          <CompactEntry key={e.num} entry={e} />
        ))}
      </div>

      {/* 07 — Featured */}
      <FeaturedEntry entry={featured2} />

      {/* Quote + footer credit */}
      <div className="pro-arc__quote">
        <div className="pro-arc__quote-bar" />
        <div className="pro-arc__quote-content">
          <p className="pro-arc__quote-text">
            {"\u201C"}Good software feels obvious. That{"\u2019"}s the hard part &mdash; the part most people skip.{"\u201D"}
          </p>
          <span className="pro-arc__quote-attr">— H.Z.</span>
          <span className="pro-arc__foot-text">
            All releases documented on joomshaper.com · 01–07 of ongoing contribution
          </span>
        </div>
      </div>
    </section>
  );
};

export default ProfessionalArc;
